import { useState } from 'react';
import api from '../services/api.js';

/**
 * useImpersonation — manages admin "View As" session state.
 *
 * State lives in sessionStorage so impersonation dies when the tab closes.
 * The MSAL token is never replaced — the api.js interceptor overrides the
 * Authorization header whenever an impersonation session is active.
 */
export function useImpersonation() {
  const [impersonation, setImpersonation] = useState(() => {
    try {
      const stored = sessionStorage.getItem('impersonation');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const startImpersonation = async (userId) => {
    const res = await api.post(`/api/users/${userId}/impersonate`);
    const data = {
      token:            res.data.token,
      impersonatedUser: res.data.impersonatedUser,
      startedAt:        Date.now(),
    };
    sessionStorage.setItem('impersonation', JSON.stringify(data));
    setImpersonation(data);
    // Full reload so all module-level caches (useCurrentUser, etc.) reset
    // and every component re-renders with the impersonated user's role.
    window.location.href = '/dashboard';
  };

  const endImpersonation = async (endReason = 'manual') => {
    try {
      await api.post('/api/users/impersonate/end', { endReason });
    } catch {
      // Always clear the local session even if the API call fails (e.g. expired token)
    }
    sessionStorage.removeItem('impersonation');
    setImpersonation(null);
    window.location.href = '/dashboard';
  };

  return { impersonation, startImpersonation, endImpersonation };
}
